import { useState } from "react"

import { TooltipView } from "@/domains/common/components/tooltip-view"

import type { LockItemDTO } from "./types"

interface PowerBreakdownBarViewProps {
  selectedLocks: LockItemDTO[]
  totalPower: number
  onRemoveLock?: (lockId: number) => void
}

/**
 * Pure View component that displays a power breakdown bar using divs.
 * Shows the percentage distribution of voting power across selected NFTs,
 * sorted from largest to smallest (left to right).
 * Uses a single gradient background with transparent segments and borders.
 */
export const PowerBreakdownBarView = ({ selectedLocks, totalPower, onRemoveLock }: PowerBreakdownBarViewProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  if (selectedLocks.length === 0) {
    return (
      <div className="w-full">
        <div className="flex h-12 w-full items-center justify-center rounded-xl border border-p400-light bg-p500 text-sm italic text-t300 shadow-inner">
          No NFTs Selected
        </div>
        <div className="mt-2 flex justify-between px-1 text-[10px] text-t300">
          <span>Largest Holdings</span>
          <span>Smallest Holdings</span>
        </div>
      </div>
    )
  }

  // Sort largest to smallest for visual stacking order
  const sortedLocks = [...selectedLocks].sort((a, b) => b.lockValue - a.lockValue)

  // Calculate cumulative positions for div segments
  let cumulativeX = 0
  const segments = sortedLocks.map((lock, index) => {
    const percentage = (lock.lockValue / totalPower) * 100
    const x = cumulativeX
    cumulativeX += percentage

    return {
      lock,
      percentage,
      x,
      width: percentage,
      index,
    }
  })

  return (
    <div className="w-full">
      {/* Bar Container with Gradient Background */}
      <div className="relative h-9 w-full overflow-visible rounded-xl border border-p400-light shadow-inner">
        {/* Single gradient background from primary-600 to primary-400 */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-400 to-primary-300" />

        {/* Transparent segments with borders */}
        <div className="relative flex h-full w-full">
          {segments.map((segment, idx) => {
            const isHovered = hoveredIndex === segment.index
            const isLast = idx === segments.length - 1

            return (
              <div
                key={segment.lock.id}
                className="h-full cursor-pointer bg-transparent transition-opacity"
                style={{
                  width: `${segment.percentage}%`,
                  borderRight: isLast ? "none" : "1px solid hsl(var(--p400-light))",
                  opacity: isHovered ? 1 : 0.95,
                }}
                onMouseEnter={() => setHoveredIndex(segment.index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            )
          })}
        </div>

        {/* Tooltip */}
        {hoveredIndex !== null && segments[hoveredIndex] && (
          <TooltipView leftPercent={segments[hoveredIndex]!.x + segments[hoveredIndex]!.percentage / 2}>
            <span className="mb-0.5 font-bold text-primary-400">NFT #{segments[hoveredIndex]!.lock.id}</span>
            <span className="font-mono">
              {segments[hoveredIndex]!.lock.lockValue.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}{" "}
              veSCALE
            </span>
          </TooltipView>
        )}
      </div>

      {/* Helper Legend Text */}
      <div className="mt-2 flex justify-between px-1 text-[10px] text-t300">
        <span>Largest Holdings</span>
        <span>Smallest Holdings</span>
      </div>
    </div>
  )
}
